import { isAfter } from 'date-fns';
import { groupBy, sumBy } from 'lodash-es';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';
import {
  AggregatedSaleMetric,
  AggregateMetricsTimePeriod,
  Sale,
} from 'src/WebApiController';

import { SaleChartData } from '../DataInsightsContext';
import { formatByGranularity } from './groupSalesByGranularity';
import {
  emptyMarketplaceData,
  getMarketplaceIdFromMarketplace,
} from './stitchChartData';

export const stitchNonCumulativeChartData = (
  aggregatedHistoricalData: AggregatedSaleMetric[] | null | undefined,
  recentSalesData: Sale[] | null | undefined,
  timeZone: string,
  timePeriod: AggregateMetricsTimePeriod,
  useDecreasedGranularity = false
): SaleChartData[] | null => {
  if (
    !aggregatedHistoricalData ||
    !recentSalesData ||
    aggregatedHistoricalData.length < 1
  )
    return null;

  const allRecentSales = recentSalesData;

  const latestMaxSaleTimestamp = aggregatedHistoricalData.reduce(
    (latest, row) => {
      const dt = new Date(row.maxSaleTimestamp);
      return dt > latest ? dt : latest;
    },
    new Date(0)
  );

  const filteredRecentSales = allRecentSales.filter((sale) =>
    isAfter(new Date(stringToUtcDate(sale.saleDate)), latestMaxSaleTimestamp)
  );
  const groupedHistoricalByDay = groupBy(aggregatedHistoricalData, (r) =>
    formatByGranularity(
      r.dateTime,
      timePeriod,
      timeZone,
      useDecreasedGranularity
    )
  );

  const groupedRecentByDay = groupBy(filteredRecentSales, (s) =>
    formatByGranularity(
      s.saleDate,
      timePeriod,
      timeZone,
      useDecreasedGranularity
    )
  );

  const allDays = new Set([
    ...Object.keys(groupedHistoricalByDay),
    ...Object.keys(groupedRecentByDay),
  ]);

  const stitched: SaleChartData[] = [];

  for (const day of Array.from(allDays).toSorted()) {
    const historical = groupedHistoricalByDay[day] ?? [];
    const recent = groupedRecentByDay[day] ?? [];

    const totalRevenue =
      sumBy(historical, (r) => r.totalRevenue) +
      sumBy(recent, (s) => s.ttlNetProcs?.amt ?? 0);

    const totalPnl =
      sumBy(historical, (r) => r.totalPnL) +
      sumBy(recent, (s) => s.pandL?.amt ?? 0);

    const totalQuantity =
      sumBy(historical, (r) => r.totalQuantity) +
      sumBy(recent, (s) => s.qtySold);

    const marketshareQuantity: Record<string, number> = {
      ...emptyMarketplaceData,
    };

    historical.forEach((h) => {
      if (h.marketplaceId) {
        marketshareQuantity[h.marketplaceId] =
          (marketshareQuantity[h.marketplaceId] || 0) + (h.totalQuantity || 0);
      }
    });

    recent.forEach((r) => {
      const marketplaceId = getMarketplaceIdFromMarketplace(r.mkp);
      if (marketplaceId) {
        marketshareQuantity[marketplaceId] =
          (marketshareQuantity[marketplaceId] || 0) + (r.qtySold || 0);
      }
    });

    const rawMarketsharePercentages = Object.entries(
      marketshareQuantity
    ).reduce<Record<string, number>>(
      (marketshareAcc, [marketplaceId, quantity]) => {
        const marketsharePercentage =
          totalQuantity > 0
            ? parseFloat((quantity / totalQuantity).toFixed(2))
            : 0;

        marketshareAcc[marketplaceId] = marketsharePercentage;
        return marketshareAcc;
      },
      {}
    );

    stitched.push({
      dateTime: day,
      totalRevenue,
      totalPnl,
      totalQuantity,
      marketshareQuantity,
      marketshare: rawMarketsharePercentages,
    });
  }

  return stitched;
};
